import { ArticleApiResponse, RelatedPostsResponse } from 'api/api.types';
import { getApiToken } from 'api/apiRequest';
import { getArticle, getRelatedPosts } from 'api/Article';
import useRouter from 'hooks/useRouter';
import { GetServerSideProps, NextPage } from 'next';
import getConfig from 'next/config';
import useProfile from 'providers/hooks/useProfile';
import React, { useEffect } from 'react';
import SourcePage from 'src/page-components/source/SourcePage';
import { Analytics, Events } from 'src/services/analytics';
import { ArticleStatus } from 'types/enums';
import { slugify } from 'utils/global';
import { getArticlePageMetadata } from 'utils/metadata.utils';
import { normalizeSource } from 'utils/normalizers/source';

interface InitialProps {
  source: ArticleApiResponse;
  /**
   * If true, after this article loads, if it belongs to the current user, a new idea will be added
   */
  startInEditMode: boolean;
  /**
   * If true, the initial request is done logged in
   * @default false
   */
  isInitialLoggedIn?: boolean;
  relatedPosts?: RelatedPostsResponse;
}

const ArticleDynamicPage: NextPage<InitialProps> = ({
  source,
  startInEditMode,
  isInitialLoggedIn,
  relatedPosts,
}) => {
  const router = useRouter();
  const { profile } = useProfile();

  const normalizedSource = normalizeSource(source);

  useEffect(() => {
    if (source && isInitialLoggedIn && router.query.idea) {
      router.replace(
        `/article/${source.id}/${slugify(source.title)}` +
          '#idea_' +
          router.query.idea,
        undefined,
        {
          shallow: true,
        },
      );
    }
  }, [source, isInitialLoggedIn, router.query.idea]);

  useEffect(() => {
    if (source) {
      if (profile && profile.id === source.id) {
        Analytics.logEvent({
          eventName: Events.creation.viewImported,
          properties: {
            articleId: source?.id,
            title: source?.title,
          },
          platforms: ['amplitude'],
        });
      } else {
        const { utm_source, utm_medium } = router.query;

        Analytics.logEvent({
          eventName: Events.consumption.viewArticle,
          properties: {
            articleId: source.id,
            title: source.title,
            ...(typeof utm_source === 'string' ? { utm_source } : {}),
            ...(typeof utm_medium === 'string' ? { utm_medium } : {}),
          },
          platforms: ['amplitude'],
        });
      }
    }
  }, [source?.id]);

  return (
    <SourcePage
      startInEditMode={startInEditMode}
      source={normalizedSource}
      similarSources={source.similar_articles?.map(similarSource =>
        normalizeSource(similarSource),
      )}
      pageMetadata={getArticlePageMetadata({ article: source })}
      isInitialLoggedIn={isInitialLoggedIn}
      relatedPosts={relatedPosts}
    />
  );
};

export const getServerSideProps: GetServerSideProps<InitialProps> = async ({
  params,
  req,
  query,
}) => {
  if (!params) return { notFound: true };

  const sourceId = Number(params.id);

  const token = getApiToken({ requestServer: req, allowDefault: true });

  const source = await getArticle({
    articleId: sourceId,
    simple: false,
    similarSourcesCount: 3,
    nextRequest: req,
  }).catch(() => undefined);
  const shouldStartInEditMode = query.edit !== undefined;

  if (!source) return { notFound: true };
  const slug = slugify(source.title, source.content);

  if (source.status === ArticleStatus.DRAFT) {
    // This is a draft so we send the user the editor-image-picker
    //We'll later check if this source is owned by the current user
    return {
      redirect: {
        destination: `/studio/draft/${sourceId}`,
        statusCode: 307,
      },
    };
  }

  if (params.slug !== slug) {
    //Redirect to the correct slug
    return {
      redirect: {
        destination: `/article/${sourceId}/${slug}${
          shouldStartInEditMode ? '?edit=true' : ''
        }`,
        statusCode: 301,
      },
    };
  }

  const isInitialLoggedIn = token !== getConfig().publicRuntimeConfig.API_TOKEN;

  const relatedPosts = await getRelatedPosts({ postId: sourceId }).catch(
    () => undefined,
  );

  return {
    props: {
      isInitialLoggedIn,
      source,
      startInEditMode: shouldStartInEditMode,
      relatedPosts,
    },
  };
};

export default ArticleDynamicPage;
